import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import {
    useGetOAuthIntegrationsQuery,
    usePostOAuthIntegrationRequestMutation,
} from '@/core/api/rapidstor';
import { OAuthIntegration } from '@/core/types/OAuthIntegration';

import styles from './OAuthIntegrations.module.scss';

interface OAuthIntegrationsProps {
    continueTo?: string;
    metadata?: Record<string, unknown>;
    onIntegrationSuccess?: (integration: OAuthIntegration, redirectUrl: string) => void;
    onIntegrationError?: (integration: OAuthIntegration, error: string) => void;
    className?: string;
    style?: React.CSSProperties;
    integrations?: OAuthIntegration[]; // Optional prop for providing mock data
}

function OAuthIntegrations({
    continueTo = window.location.href,
    metadata = {},
    onIntegrationSuccess,
    onIntegrationError,
    className = '',
    style = {},
    integrations: providedIntegrations,
}: OAuthIntegrationsProps) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const [loadingIntegrationId, setLoadingIntegrationId] = useState<number | null>(null);

    const {
        data: fetchedIntegrations,
        isLoading,
        error: fetchError,
    } = useGetOAuthIntegrationsQuery({
        locationCode: location.locationCode,
        corpCode: location.corpCode,
    }, {
        skip: !!providedIntegrations, // Skip the query if integrations are provided
    });

    // Use provided integrations if available, otherwise use fetched data
    const integrations = providedIntegrations || fetchedIntegrations;
    const [postOAuthRequest, { error: postError, isLoading: isProcessing }] = usePostOAuthIntegrationRequestMutation();

    const handleIntegrationClick = async (integration: OAuthIntegration) => {
        if (loadingIntegrationId || isProcessing) {
            return;
        }
        setLoadingIntegrationId(integration.id);

        try {
            const response = await postOAuthRequest({
                locationCode: location.locationCode,
                corpCode: location.corpCode,
                payload: {
                    integrationId: integration.id,
                    continueTo,
                    metadata,
                },
            }).unwrap();

            if (onIntegrationSuccess) {
                onIntegrationSuccess?.(integration, response.url);
            } else {
                window.location.href = response.url;
            }
        } catch (error) {
            // Error is now handled by transformErrorResponse in the API
            // The error will be available in the postError from the hook
            const errorMessage = typeof error === 'string' ? error : 'OAuth integration request failed';
            onIntegrationError?.(integration, errorMessage);
            setLoadingIntegrationId(null);
        }
    };

    const getButtonText = (integration: OAuthIntegration): string => {
        const userLanguage = navigator.language.split('-')[0];

        return (
            integration.button_text[userLanguage] ||
            integration.button_text.en ||
            integration.button_text[Object.keys(integration.button_text)[0]] ||
            integration.public_name
        );
    };

    if (!providedIntegrations && isLoading) {
        return null;
    }

    if (!providedIntegrations && fetchError) {
        return null;
    }

    if (!integrations || integrations.length === 0) {
        return null;
    }

    let errorMessage: string | null = null;
    if (postError) {
        errorMessage = typeof postError === 'string'
            ? postError
            : 'OAuth integration request failed';
    }

    return (
        <div className={`${styles.oauthIntegrations} ${className}`} style={style}>
            {integrations.map((integration) => (
                <button
                    key={integration.id}
                    type="button"
                    className={styles.oauthIntegrationButton}
                    onClick={() => handleIntegrationClick(integration)}
                    disabled={loadingIntegrationId !== null || isProcessing}
                    style={{
                        backgroundColor: integration.button_colour,
                        color: integration.button_text_colour,
                    }}
                    title={integration.public_name}
                >
                    {integration.button_icon_url && (
                        <img
                            src={integration.button_icon_url}
                            alt={`${integration.public_name} icon`}
                            onError={(e) => {
                                // Hide image if it fails to load
                                const target = e.currentTarget;
                                target.style.display = 'none';
                            }}
                        />
                    )}
                    <span>
                        {loadingIntegrationId === integration.id
                            ? t('oauth:connecting')
                            : getButtonText(integration)}
                    </span>
                </button>
            ))}
            {errorMessage && (
                <div className={`${styles.oauthIntegrationsError} ${className}`}>
                    <p>{errorMessage}</p>
                </div>
            )}
        </div>
    );
}

export default OAuthIntegrations;
